import type { BrandPair } from '@/config/brand';
import { adjust, alpha, contrastText, mix, toAccent } from './colors';

export type ThemeMode = 'light' | 'dark';

export type BrandTokens = ReturnType<typeof buildTokens>;

/** Dotted texture for the login backdrop, tinted for the gradient it sits on. */
const dotPattern = (color: string, opacity: number) =>
    `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${color.replace(
        '#',
        '%23'
    )}' fill-opacity='${opacity}'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

/**
 * Derive the full token set for one mode from the two configured brand colours.
 * Nothing below is hand-picked per brand, so swapping the pair in
 * `src/config/brand.ts` re-colours the whole interface coherently.
 */
export const buildTokens = (mode: ThemeMode, pair: BrandPair) => {
    const isDark = mode === 'dark';

    // Readable siblings of the brand colours for use on app surfaces.
    const accentPrimary = toAccent(pair.primary, mode);
    const accentSecondary = toAccent(pair.secondary, mode);

    // Text that sits directly on the brand gradient (app bar, login backdrop).
    const onBrand = contrastText(mix(pair.primary, pair.secondary, 0.5));
    const onBrandIsLight = onBrand === '#FFFFFF';

    const surface = isDark
        ? { base: '#0A0F1C', raised: '#111A2B', raisedHigh: '#162034' }
        : { base: '#F4F6FB', raised: '#FFFFFF', raisedHigh: '#FFFFFF' };

    const hairline = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.10)';
    const hairlineStrong = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.17)';

    const text = isDark
        ? { primary: '#EEF2F9', secondary: '#94A3B8' }
        : { primary: '#101828', secondary: '#55607A' };

    return {
        mode,
        pair,
        accent: { primary: accentPrimary, secondary: accentSecondary },
        onBrand,
        text,
        hairlineColor: hairline,
        hairlineStrongColor: hairlineStrong,

        gradients: {
            // Brand identity: the configured pair, untouched.
            primary: `linear-gradient(135deg, ${pair.primary} 0%, ${pair.secondary} 100%)`,
            // Accent for controls and highlights on app surfaces.
            accent: `linear-gradient(135deg, ${accentPrimary} 0%, ${accentSecondary} 100%)`,
            accentHover: `linear-gradient(135deg, ${adjust(accentPrimary, {
                dl: isDark ? -0.06 : -0.05,
            })} 0%, ${adjust(accentSecondary, { dl: isDark ? -0.06 : -0.05 })} 100%)`,
            // Ambient brand light behind the page.
            ambient: isDark
                ? `radial-gradient(1100px 620px at 12% -12%, ${alpha(accentPrimary, 0.22)}, transparent 62%),
                   radial-gradient(900px 520px at 102% -6%, ${alpha(accentSecondary, 0.14)}, transparent 58%),
                   linear-gradient(180deg, ${surface.base} 0%, ${adjust(surface.base, { dl: 0.03 })} 100%)`
                : `radial-gradient(1100px 620px at 12% -12%, ${alpha(accentPrimary, 0.14)}, transparent 62%),
                   radial-gradient(900px 520px at 102% -6%, ${alpha(accentSecondary, 0.10)}, transparent 58%),
                   linear-gradient(180deg, ${surface.base} 0%, #FFFFFF 100%)`,
            // Card titles are white over artwork in both modes, so the scrim
            // stays dark regardless of mode.
            scrim: 'linear-gradient(to top, rgba(8,12,22,0.94) 12%, rgba(8,12,22,0.55) 48%, rgba(8,12,22,0.12) 100%)',
            // Top highlight for raised surfaces. In light mode the surface is
            // already white, so a white highlight is a no-op there - and layering
            // it over anything coloured just washes the top out. Keep it to dark.
            sheen: isDark
                ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 45%)'
                : 'none',
            footer: `linear-gradient(to top, ${alpha(surface.base, 0.85)} 0%, transparent 100%)`,
        },

        borders: {
            hairline: `1px solid ${hairline}`,
            hairlineStrong: `1px solid ${hairlineStrong}`,
            divider: `1px solid ${hairline}`,
            accent: `1px solid ${alpha(accentPrimary, 0.4)}`,
        },

        backgrounds: {
            base: surface.base,
            raised: surface.raised,
            raisedHigh: surface.raisedHigh,
            glass: isDark ? alpha('#111A2B', 0.72) : 'rgba(255,255,255,0.78)',
            // Sits over card artwork, so it stays dark in both modes.
            glassStrong: 'rgba(8,12,22,0.62)',
            glassHover: alpha(accentPrimary, 0.22),
            iconBox: onBrandIsLight ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
            // Controls sitting on the brand gradient.
            onBrandGhost: onBrandIsLight ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)',
            onBrandGhostHover: onBrandIsLight ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.14)',
            onBrandBorder: onBrandIsLight ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.16)',
            onBrandBorderStrong: onBrandIsLight ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.24)',
        },

        effects: {
            backdropBlur: 'blur(12px)',
            // The card itself never moves or scales on hover - the border
            // highlight and glow carry the state. Set a transform here to
            // re-enable a lift.
            hoverTransform: 'none',
            // Only the card artwork zooms, clipped by its own overflow box.
            mediaZoom: 'scale(1.07)',
            shadowSoft: isDark
                ? '0 1px 2px rgba(0,0,0,0.30), 0 8px 24px -12px rgba(0,0,0,0.55)'
                : '0 1px 2px rgba(16,24,40,0.05), 0 8px 24px -14px rgba(16,24,40,0.20)',
            shadowLift: isDark
                ? '0 2px 4px rgba(0,0,0,0.30), 0 22px 48px -18px rgba(0,0,0,0.75)'
                : '0 2px 4px rgba(16,24,40,0.06), 0 22px 48px -20px rgba(16,24,40,0.26)',
            shadowGlow: `0 22px 48px -20px ${alpha(accentPrimary, isDark ? 0.55 : 0.38)}`,
        },

        transitions: {
            standard: 'all 0.18s cubic-bezier(.4,0,.2,1)',
            smooth: 'all 0.32s cubic-bezier(.4,0,.2,1)',
        },

        patterns: {
            dots: dotPattern(onBrandIsLight ? '#ffffff' : '#000000', onBrandIsLight ? 0.05 : 0.06),
        },
    };
};
